import { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  Award,
  Ban,
  Bot,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Clock,
  Hotel,
  Link2,
  Loader2,
  Moon,
  Plane,
  RotateCw,
  ShieldAlert,
  User,
  WifiOff,
  XCircle,
} from 'lucide-react';
import { Link } from 'react-router';
import { Trans, useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  useDisruptionProgress,
  useDisruptionRoster,
  useRebookingDetail,
} from '@/lib/disruptions/use-disruptions';
import { formatFlightTime } from '@/lib/disruptions/airport-tz';
import { usePolicies } from '@/lib/policies/use-policies';
import { useConnectionStore } from '@/state/connection.store';
import type {
  DisruptionProgress,
  DisruptionPnrGroupRow,
  RebookingGroupResult,
  RebookingOptionRow,
  RebookingScored,
  RebookingSettleOutcome,
} from '@/lib/disruptions/types';
import {
  formatPriceMinor,
  noOptionsReasonLabel,
  noOptionsReasonShort,
  operatorLabel,
  vendorListLabel,
} from './labels';
import { PnrEvaluationTimeline } from './pnr-evaluation-timeline';

/** A per-PNR outcome merged from the REST snapshot + the live WS settle feed —
 *  both share the group's fields; `optionCount` is normalised across the two. */
type MergedOutcome = RebookingSettleOutcome & { optionCount: number };

function toMerged(g: RebookingGroupResult | RebookingSettleOutcome): MergedOutcome {
  const optionCount = 'optionCount' in g ? g.optionCount : g.options.length;
  return { ...g, optionCount };
}

/** Per-group states that count as "settled" for the feed (a result to show), vs the
 *  transient PENDING that stays out of the feed until it settles. */
const SETTLED_ROSTER_STATUSES = new Set([
  'SELECTED',
  'OVERRIDDEN',
  'OPTIONS_READY',
  'NO_OPTIONS',
  'NOT_APPLICABLE',
]);

/** Minimal roster row synthesized from a live settle outcome — for feed rows the paged
 *  roster hasn't loaded yet (the WS settle stream leads the roster). Only the fields the
 *  feed row + its summary/no-options lines read are meaningful; the rest are defaulted. */
function synthGroupFromOutcome(o: MergedOutcome): DisruptionPnrGroupRow {
  return {
    pnrLabel: o.pnrMasked,
    pnrUrn: '',
    subCaseUrn: o.groupId,
    caseUrn: '',
    flightLabel: o.original?.flightLabel ?? '',
    flightUrn: '',
    flightNumber: '',
    origin: o.original?.originIata ?? '',
    destination: o.original?.destIata ?? '',
    departureAt: '',
    rebookingStatus: o.status as DisruptionPnrGroupRow['rebookingStatus'],
    optionsCount: o.optionCount ?? o.options.length,
    paxCount: o.paxCount ?? 0,
    paxBreakdown: { adt: 0, chd: 0, inf: 0 },
    priorityTags: [],
    leadName: '',
    passengers: [],
    ...(o.noOptionsReason !== undefined ? { noOptionsReason: o.noOptionsReason } : {}),
  };
}

/** i18n key per strategy/option kind (`best.kind` / option `kind`) — keys, never language. */
const STRATEGY_KIND_KEY: Record<string, string> = {
  OWN_FLIGHT: 'strategy.kind.OWN_FLIGHT',
  INTERLINE: 'strategy.kind.INTERLINE',
  OWN_WITH_LAYOVER: 'strategy.kind.OWN_WITH_LAYOVER',
  INTERLINE_WITH_LAYOVER: 'strategy.kind.INTERLINE_WITH_LAYOVER',
  HOTEL_THEN_FLIGHT: 'strategy.kind.HOTEL_THEN_FLIGHT',
  SPLIT: 'strategy.kind.SPLIT',
  AIRLINE_EXTERNAL: 'strategy.kind.AIRLINE_EXTERNAL',
};

function strategyKindLabel(kind: string, t: TFunction<'rebooking'>): string {
  const key = STRATEGY_KIND_KEY[kind];
  return key ? t(key) : kind.replace(/_/g, ' ').toLowerCase();
}

/** Letter badge (A/B/C/D…) for a strategy rank — MVP style. */
function rankLetter(rank: number): string {
  const i = Math.max(1, rank) - 1;
  return String.fromCharCode(65 + (i % 26));
}

// Live bulk-run dialog (NEX-299 follow-up, redesigned). Triggering a bulk run opens
// THIS modal, which shows the run climbing live over the WS (no poll). It's wider
// and PNR-framed (not "thousands of flights" = the raw option count): three metric
// groups — flights assigned, PNRs with options, and PNRs with NO options broken
// down BY REASON (policy-disabled / no-inventory / …) — plus a mixed live feed of
// assigned AND no-options PNRs. OP-AWARE: a rebooking run shows flights; an
// allocation run keeps the hotel framing.
//
// The by-reason breakdown + the feed are derived CLIENT-SIDE from the disruption
// roster (each PNR row carries rebookingStatus / optionsCount / noOptionsReason).
// The dialog auto-pages the roster while open (bounded) so the counts cover the run;
// a server-side per-reason aggregate on the progress endpoint is the durable fix for
// very large (multi-thousand PNR) runs.

/** Which bulk op this dialog is reporting — drives all the op-specific copy. */
export type BulkRunOp = 'rebooking' | 'allocation';

interface RebookingProgressDialogProps {
  disruptionUrn: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The op that was triggered. Defaults to rebooking for back-compat. */
  op?: BulkRunOp;
  /**
   * Re-run the bulk op (FAILED / PARTIAL terminal). When provided, the dialog shows
   * a "Re-run" action on a failed/partial run so the operator retries the
   * groups a rate-limited vendor couldn't serve — without leaving the dialog.
   */
  onRerun?: () => void;
}

type FeedFilter = 'all' | 'assigned' | 'no-options';

export function RebookingProgressDialog({
  disruptionUrn,
  open,
  onOpenChange,
  op = 'rebooking',
  onRerun,
}: RebookingProgressDialogProps) {
  const { t } = useTranslation('rebooking');
  const qc = useQueryClient();
  const isAllocation = op === 'allocation';
  // Live snapshot — seeded once on open, then updated by the WS push.
  const { data } = useDisruptionProgress(disruptionUrn, open);
  // The full roster, loaded while the dialog is open — the source for the metric
  // breakdown + the live feed. Auto-paged below (bounded) so the counts cover the run.
  const roster = useDisruptionRoster(disruptionUrn, {}, open);
  const rows = roster.items.filter(Boolean) as DisruptionPnrGroupRow[];

  // Rich per-PNR rebooking detail (NEX-364 / U1) — the one-shot open read that seeds
  // score components / strategies / original itinerary. Live settle outcomes then
  // arrive over the WS progress feed (data.settledGroups) and are merged into this
  // cache by applyEvent — NEVER a poll (realtime = WS). Rebooking-only.
  const detail = useRebookingDetail(disruptionUrn, open && !isAllocation);

  // WS liveness — drives the "reconnecting" affordance (uxChecklist: WS-drop). The
  // socket store is the single source of truth for connection state (§5.1).
  const wsConnected = useConnectionStore((s) => s.connected);

  // Merge the REST snapshot rows with the live WS settle outcomes, keyed by groupId
  // (=== the PNR's subCaseUrn). A settle OVERLAYS the seed (freshest wins). This map
  // enriches each feed row with its score / strategies / original + drives the drill.
  const outcomeByGroupId = useMemo(() => {
    const m = new Map<string, MergedOutcome>();
    for (const g of detail.data?.groups ?? []) m.set(g.groupId, toMerged(g));
    for (const s of data?.settledGroups ?? []) m.set(s.groupId, toMerged(s));
    return m;
  }, [detail.data?.groups, data?.settledGroups]);

  // Which PNR row is expanded into its full "engine reasoning" detail (one at a time).
  const [expandedPnr, setExpandedPnr] = useState<string | null>(null);

  // Pull the roster forward: load all pages while open (capped to keep a 10k-PNR run
  // from hammering the BFF — the counts then cover the loaded head + a "+ more" hint).
  useEffect(() => {
    if (open && roster.hasNextPage && !roster.isFetchingNextPage && rows.length < 1500) {
      roster.fetchNextPage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, roster.hasNextPage, roster.isFetchingNextPage, rows.length]);

  // NOTE: the metric COUNTS come from the WS-pushed progress (`data`), NOT from the
  // roster — so there is no client timer here. The run's group-states are
  // aggregated server-side (run-synchronous), so the tiles are exact the moment the
  // run lands, with no async cases-denorm lag. The roster (above) backs only the
  // per-PNR live FEED, refreshed by the existing WS roster invalidation (apply-event).

  const [feedFilter, setFeedFilter] = useState<FeedFilter>('all');
  const now = useTickWhile(
    open &&
      (data?.runStatus === 'RUNNING' ||
        data?.runStatus === 'QUEUED' ||
        data?.allocationRun?.status === 'RUNNING'),
  );

  // A hotel-allocation dialog follows BOOKING's durable run marker (`allocationRun`);
  // the rebooking `runStatus` says nothing about hotels — reading it here is how the
  // dialog said "completed · 0 hotels" the instant the trigger returned, 70 s
  // before the worker had placed a single hold.
  const alloc = isAllocation ? (data?.allocationRun ?? null) : null;
  const running = isAllocation
    ? alloc?.status === 'RUNNING'
    : data?.runStatus === 'RUNNING' || data?.runStatus === 'QUEUED';
  const done = isAllocation ? alloc?.status === 'COMPLETED' : data?.runStatus === 'COMPLETED';
  // Honest terminal states (resilience hardening): a run that hard-failed, or one
  // that generated but left some groups degraded by a rate-limited/erroring vendor.
  const failed = isAllocation ? alloc?.status === 'FAILED' : data?.runStatus === 'FAILED';
  const partial = !isAllocation && data?.runStatus === 'PARTIAL';
  const degradedCount = data?.degradedGroups ?? 0;
  // Live vendor delay (E): a throttled provider while the run is in flight — PNRs
  // are slower, NOT failed. Empty unless RUNNING (the BFF gates it on the run state).
  const delayedBy = running ? (data?.delayedBy ?? []) : [];
  // Latency-driven slowness (not a 429) — the "why is it slow" signal when no vendor
  // is rate-limited. A vendor can't be in both lists (the BFF excludes throttled).
  const slowBy = running ? (data?.slowBy ?? []) : [];

  // ── Metric COUNTS — authoritative from the WS-pushed progress (server-aggregated
  //    from the run's group-states; run-synchronous → exact at completion, no timer).
  const assignedCount = data?.assignedGroups ?? 0;
  const withOptionsCount = data?.groupsWithOptions ?? 0;
  const noOptionsCount = data?.noOptionGroups ?? 0;
  const noOptionsByReason = useMemo(
    () => Object.entries(data?.noOptionsByReason ?? {}).sort((a, b) => b[1] - a[1]),
    [data?.noOptionsByReason],
  );

  // ── Live rebooking feed — driven by the WS settle outcomes (outcomeByGroupId), so
  //    each PNR appears AS the run settles it. The paged roster (`rows`) only refreshes
  //    on a TERMINAL run, so a roster-driven feed stayed EMPTY mid-run; the settle
  //    stream (progress-settle@v2) is live. Enrich each settled group with its roster
  //    row when the roster has caught up, else synthesize a minimal row from the
  //    outcome. All status branching keys on the LIVE outcome status, not the roster.
  const rowBySubCase = useMemo(() => {
    const m = new Map<string, DisruptionPnrGroupRow>();
    for (const r of rows) m.set(r.subCaseUrn, r);
    return m;
  }, [rows]);
  const settledRows = useMemo(() => {
    const seen = new Set<string>();
    const list: DisruptionPnrGroupRow[] = [];
    // 1) live settle outcomes (WS progress-settle + REST rebooking-detail) — the leading
    //    edge: a PNR appears here AS the engine settles it, before the paged roster catches up.
    for (const o of outcomeByGroupId.values()) {
      seen.add(o.groupId);
      list.push(rowBySubCase.get(o.groupId) ?? synthGroupFromOutcome(o));
    }
    // 2) fallback — roster rows already in a settled state but without an outcome yet
    //    (degraded rebooking-detail, or an older run whose WS settle stream is gone).
    for (const r of rows) {
      if (seen.has(r.subCaseUrn) || !SETTLED_ROSTER_STATUSES.has(r.rebookingStatus)) continue;
      list.push(r);
    }
    return list;
  }, [outcomeByGroupId, rowBySubCase, rows]);
  const assignedRows = useMemo(
    () =>
      settledRows.filter((g) => {
        const s = outcomeByGroupId.get(g.subCaseUrn)?.status ?? g.rebookingStatus;
        return s === 'SELECTED' || s === 'OVERRIDDEN';
      }),
    [settledRows, outcomeByGroupId],
  );
  const noOptionRows = useMemo(
    () =>
      settledRows.filter((g) => {
        const s = outcomeByGroupId.get(g.subCaseUrn)?.status ?? g.rebookingStatus;
        return s === 'NO_OPTIONS' || s === 'NOT_APPLICABLE';
      }),
    [settledRows, outcomeByGroupId],
  );

  // ── Allocation feed (unchanged semantics — hotels reserved) ─────────────────────
  const hotelFeed = useMemo(() => rows.filter((g) => !!g.hotelReservationUrn), [rows]);
  const neededUnreserved = isAllocation
    ? rows.filter((g) => g.requiresHotel === true && !g.hotelReservationUrn).length
    : 0;

  // ── Progress framing — PNR groups, not cases (counts from the WS progress) ───────
  const total = data?.groupsTotal ?? 0;
  const processed = assignedCount + withOptionsCount + noOptionsCount;
  const pct = data ? (isAllocation ? allocationPct(alloc) : runPct(data)) : 0;

  // ── Live per-group progress (NEX — legible slow runs). While a run is in flight,
  //    show how far through the groups it is + elapsed + throughput, so a slow run
  //    reads as "working, group N of M · ~K/min" instead of a frozen bar.
  //    An allocation run counts booking's own settled groups (never the rebooking
  //    counters — those belong to the previous run and read as a phantom ~N/min).
  const liveProcessed = isAllocation ? (alloc?.processedGroups ?? 0) : (data?.processedGroups ?? 0);
  const livePlanned = isAllocation ? (alloc?.totalGroups ?? 0) : (data?.plannedGroups ?? 0);
  const elapsedSecs = elapsedSeconds(data?.activeRun?.startedAt, now);
  const throughputPerMin =
    running && elapsedSecs >= 5 && liveProcessed > 0
      ? Math.max(1, Math.round((liveProcessed / elapsedSecs) * 60))
      : null;

  // Closing the dialog refreshes the Passengers grid once (the operator returns to it
  // expecting the new states) — replaces the per-tick grid invalidation.
  const handleOpenChange = (next: boolean): void => {
    if (!next) {
      void qc.invalidateQueries({ queryKey: ['disruptions', disruptionUrn, 'roster'] });
    }
    onOpenChange(next);
  };

  const titleNoun = isAllocation ? t('progress.title.allocation') : t('progress.title.rebooking');
  const TitleIcon = isAllocation ? Hotel : Plane;

  // uxChecklist states for the rich rebooking feed:
  //  - loading: initial open, nothing loaded yet → skeleton.
  //  - empty: progress is in + the disruption genuinely has 0 rebooking groups.
  const rosterLoading =
    !isAllocation && rows.length === 0 && (roster.isLoading || detail.isLoading);
  const isEmptyDisruption =
    !!data &&
    !running &&
    total === 0 &&
    rows.length === 0 &&
    outcomeByGroupId.size === 0;

  const feed = isAllocation
    ? hotelFeed
    : feedFilter === 'assigned'
      ? assignedRows
      : feedFilter === 'no-options'
        ? noOptionRows
        : // 'all' → EVERY settled group (incl. OPTIONS_READY — the common state during a
          //  GENERATE run), so the feed fills live as the engine settles each PNR.
          settledRows;

  // The expanded PNR's engine detail opens as a RIGHT-SIDE DRAWER (full height, pushes
  // the list left, highlights the selected row) — not an inline expand below the row.
  const selectedOutcome =
    !isAllocation && expandedPnr ? outcomeByGroupId.get(expandedPnr) : undefined;
  const selectedGroup =
    !isAllocation && expandedPnr
      ? (rowBySubCase.get(expandedPnr) ??
        (selectedOutcome ? synthGroupFromOutcome(selectedOutcome) : undefined))
      : undefined;
  const drawerOpen = !!expandedPnr && !!selectedOutcome && !!selectedGroup;

  // Near-full-screen for the rich rebooking view (per-PNR rows + expandable engine
  // detail); the allocation view keeps the compact chrome. FIXED 95vh from the start
  // (doesn't grow as PNRs arrive) as a flex column — the header/metrics stay put and
  // ONLY the PNR list scrolls internally (frontend-conventions §18), never the dialog.
  const contentClass = isAllocation
    ? 'sm:max-w-2xl'
    : 'flex h-[95vh] max-h-[95vh] w-[95vw] max-w-[95vw] flex-col overflow-hidden';

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className={contentClass} data-testid="rebooking-progress-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {running ? (
              <Loader2 className="h-4 w-4 animate-spin text-primary" aria-hidden />
            ) : done ? (
              <CheckCircle2 className="h-4 w-4 text-emerald-600" aria-hidden />
            ) : failed ? (
              <XCircle className="h-4 w-4 text-destructive" aria-hidden />
            ) : partial ? (
              <AlertTriangle className="h-4 w-4 text-amber-600" aria-hidden />
            ) : (
              <TitleIcon className="h-4 w-4 text-muted-foreground" aria-hidden />
            )}
            {titleNoun}
            {running
              ? ` · ${t('progress.title.inProgress')}`
              : done
                ? ` · ${t('progress.title.completed')}`
                : ''}
            {failed ? (
              <span
                className="rounded-full bg-destructive/15 px-2 py-0.5 text-xs font-semibold text-destructive"
                data-testid="run-badge-failed"
              >
                {t('progress.badge.failed')}
              </span>
            ) : partial ? (
              <span
                className="rounded-full bg-amber-500/15 px-2 py-0.5 text-xs font-semibold text-amber-700 dark:text-amber-400"
                data-testid="run-badge-partial"
              >
                {t('progress.badge.partial')}
              </span>
            ) : null}
            {/* WS-drop affordance (uxChecklist): the live feed is pushed over the WS,
                so when the socket drops we say so + show it's reconnecting. No crash —
                the last snapshot stays on screen until the push resumes. */}
            {open && !wsConnected ? (
              <span
                className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground"
                data-testid="rebooking-ws-reconnecting"
                title={t('progress.ws.reconnectingTitle')}
              >
                <WifiOff className="h-3 w-3 animate-pulse" aria-hidden />
                {t('progress.ws.reconnecting')}
              </span>
            ) : null}
          </DialogTitle>
        </DialogHeader>

        {/* Run attribution + LIVE elapsed/throughput (from the durable activeRun marker).
            While running we show a count-up clock + ~PNRs/min so a slow run reads as
            "working at N/min", not stalled. */}
        {data?.activeRun ? (
          <p
            className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground"
            data-testid="rebooking-progress-activity"
          >
            <span className="inline-flex items-center gap-1">
              <Clock className="h-3 w-3" aria-hidden />
              {running ? (
                <>
                  {t('progress.activity.running')} · {clockText(elapsedSecs)}
                </>
              ) : (
                <>
                  {t('progress.activity.stopped')}
                  {elapsedClock(data.activeRun.startedAt, now)
                    ? ` · ${t('progress.activity.ago', { time: elapsedClock(data.activeRun.startedAt, now) })}`
                    : ''}
                </>
              )}
            </span>
            {throughputPerMin != null ? (
              <span className="inline-flex items-center gap-1" data-testid="rebooking-progress-throughput">
                {t('progress.activity.throughput', { count: throughputPerMin })}
              </span>
            ) : null}
            <span className="inline-flex items-center gap-1">
              <User className="h-3 w-3" aria-hidden />
              {t('progress.activity.by', { name: operatorLabel(data.activeRun.operatorUrn) })}
            </span>
          </p>
        ) : null}

        {/* Honest terminal banner — a FAILED run reads as a failure (not a neutral
            100%); a PARTIAL run explains the vendor degradation + how to recover. */}
        {failed || partial ? (
          <div
            className={`flex items-start gap-2 rounded-md border p-3 text-sm ${
              failed
                ? 'border-destructive/40 bg-destructive/10 text-destructive'
                : 'border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-400'
            }`}
            data-testid="rebooking-progress-status-banner"
          >
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
            <div className="space-y-0.5">
              <p className="font-medium">
                {failed ? t('progress.banner.failedTitle') : t('progress.banner.partialTitle')}
              </p>
              <p className="text-xs opacity-90">
                {failed
                  ? t('progress.banner.failedBody', {
                      done: processed,
                      total: total || processed,
                    })
                  : t('progress.banner.partialBody', { count: degradedCount })}
              </p>
            </div>
          </div>
        ) : null}

        {/* Live vendor-delay banner (E): a provider is rate-limiting OR responding
            slowly during THIS run — the PNRs are in process, just slower. Explicitly
            NOT a failure. Explains "why is it taking more time". */}
        {delayedBy.length > 0 || slowBy.length > 0 ? (
          <div
            className="flex items-start gap-2 rounded-md border border-sky-500/40 bg-sky-500/10 p-3 text-sm text-sky-700 dark:text-sky-300"
            data-testid="rebooking-progress-delayed-banner"
          >
            <Loader2 className="mt-0.5 h-4 w-4 shrink-0 animate-spin" aria-hidden />
            <div className="space-y-0.5 text-xs">
              {delayedBy.length > 0 ? (
                <p>
                  <Trans
                    t={t}
                    i18nKey="progress.banner.delayedRateLimited"
                    values={{ vendors: vendorListLabel(delayedBy) }}
                    components={{ bold: <span className="font-medium" /> }}
                  />
                </p>
              ) : null}
              {slowBy.length > 0 ? (
                <p>
                  <Trans
                    t={t}
                    i18nKey="progress.banner.delayedSlow"
                    values={{ vendors: vendorListLabel(slowBy) }}
                    components={{ bold: <span className="font-medium" /> }}
                  />
                </p>
              ) : null}
              <p className="opacity-90">{t('progress.banner.noFailureYet')}</p>
            </div>
          </div>
        ) : null}

        {/* Progress bar — PNR-framed. */}
        <div className="flex items-center gap-3">
          <div
            className="h-2.5 flex-1 overflow-hidden rounded-sm bg-muted"
            role="progressbar"
            aria-valuenow={pct}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={t('progress.bar.ariaLabel', { noun: titleNoun })}
          >
            <div
              className={`h-full rounded-sm transition-all ${
                failed ? 'bg-destructive' : partial ? 'bg-amber-500' : 'bg-primary'
              }`}
              style={{ width: `${pct}%` }}
              data-testid="rebooking-progress-fill"
            />
          </div>
          <span className="w-12 shrink-0 text-right text-sm font-semibold tabular-nums">{pct}%</span>
        </div>

        <p className="text-sm text-muted-foreground" data-testid="rebooking-progress-counts">
          {data ? (
            isAllocation ? (
              alloc ? (
                <>
                  {t('progress.counts.pnrsProcessed', {
                    done: alloc.processedGroups,
                    total: alloc.totalGroups,
                    count: alloc.totalGroups,
                  })}
                  {' · '}
                  {t('progress.counts.cases', {
                    done: alloc.casesProcessed,
                    total: alloc.casesTotal,
                    count: alloc.casesTotal,
                  })}
                  {alloc.alreadyReservedGroups > 0
                    ? ` · ${t('progress.counts.alreadyWithHotel', { count: alloc.alreadyReservedGroups })}`
                    : ''}
                </>
              ) : (
                t('progress.counts.starting')
              )
            ) : (
              <>
                {t('progress.counts.pnrsProcessed', {
                  done: processed,
                  total: total || processed,
                  count: total,
                })}
                {(partial || failed) && degradedCount > 0 ? (
                  <span className="font-medium text-amber-600 dark:text-amber-400">
                    {' '}
                    · {t('progress.counts.degradedSuffix', { count: degradedCount })}
                  </span>
                ) : null}
              </>
            )
          ) : (
            t('progress.counts.starting')
          )}
        </p>

        {/* Live "what's happening now" line (NEX — legible slow runs): the group the
            run is on, so a slow case reads as forward motion, not a frozen bar. */}
        {running && !isAllocation && livePlanned > 0 ? (
          <p
            className="-mt-2 text-xs text-muted-foreground"
            data-testid="rebooking-progress-group-line"
          >
            {t('progress.counts.groupLine', {
              current: Math.min(liveProcessed + 1, livePlanned),
              total: livePlanned,
            })}
          </p>
        ) : null}

        {/* Metric groups — what the run is producing, live. */}
        {isAllocation ? (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Stat label={t('progress.stat.hotelHold')} value={alloc?.heldGroups ?? hotelFeed.length} tone="text-emerald-600" />
            <Stat label={t('progress.stat.noCapacity')} value={alloc?.soldOutGroups ?? 0} tone={alloc?.soldOutGroups ? 'text-amber-600' : ''} />
            <Stat label={t('progress.stat.noEligibleHotel')} value={alloc?.noOfferGroups ?? 0} tone={alloc?.noOfferGroups ? 'text-amber-600' : ''} />
            <Stat label={t('progress.stat.cases')} value={`${alloc?.casesProcessed ?? 0} / ${alloc?.casesTotal ?? 0}`} />
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <Stat label={t('progress.stat.flightsAssigned')} value={assignedCount} tone="text-emerald-600" />
            <Stat label={t('progress.stat.pnrsWithOptions')} value={withOptionsCount} />
            <NoOptionsStat total={noOptionsCount} byReason={noOptionsByReason} />
          </div>
        )}

        {/* Rich-detail fetch error (uxChecklist: error) — non-blocking + retryable.
            The rows still render from the roster/progress; only the seeded score /
            strategies / original are missing until the retry succeeds. */}
        {!isAllocation && detail.isError ? (
          <div
            className="flex items-center justify-between gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive"
            data-testid="rebooking-detail-error"
          >
            <span className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden />
              {t('progress.detailError.message')}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => void detail.refetch()}
              data-testid="rebooking-detail-retry"
            >
              <RotateCw className="mr-1.5 h-3.5 w-3.5" aria-hidden />
              {t('progress.detailError.retry')}
            </Button>
          </div>
        ) : null}

        {/* Live feed — mixed (assigned + no-options) for rebooking; hotels for allocation. */}
        <div className="flex items-center justify-between">
          <span className="text-xs font-medium text-foreground/70">
            {isAllocation ? t('progress.feed.title') : t('progress.feed.titleReasoning')}
          </span>
          {!isAllocation && settledRows.length > 0 ? (
            <select
              value={feedFilter}
              onChange={(e) => setFeedFilter(e.target.value as FeedFilter)}
              className="rounded-md border border-input bg-background px-2 py-1 text-xs"
              data-testid="rebooking-feed-filter"
              aria-label={t('progress.feed.filterLabel')}
            >
              <option value="all">{t('progress.feed.filter.all')}</option>
              <option value="assigned">{t('progress.feed.filter.assigned')}</option>
              <option value="no-options">{t('progress.feed.filter.noOptions')}</option>
            </select>
          ) : null}
        </div>
        <div className={isAllocation ? 'contents' : 'flex min-h-0 flex-1 gap-3'}>
        <div
          className={`rounded-md border border-border ${
            isAllocation ? 'max-h-64 overflow-y-auto' : 'min-h-0 flex-1 overflow-y-auto'
          }`}
          data-testid="rebooking-progress-feed"
        >
          {feed.length === 0 ? (
            // Loading skeleton (uxChecklist: loading) → empty state (uxChecklist: empty)
            // → the running/idle copy. A truly empty disruption (0 groups, terminal/none)
            // reads as a friendly empty state, not "processing…".
            !isAllocation && rosterLoading ? (
              <FeedSkeleton />
            ) : !isAllocation && isEmptyDisruption ? (
              <div
                className="flex flex-col items-center gap-1 p-6 text-center"
                data-testid="rebooking-detail-empty"
              >
                <Plane className="h-6 w-6 text-muted-foreground/60" aria-hidden />
                <p className="text-sm font-medium">{t('progress.feed.emptyTitle')}</p>
                <p className="text-xs text-muted-foreground">{t('progress.feed.emptyBody')}</p>
              </div>
            ) : (
              <p className="p-3 text-center text-xs text-muted-foreground">
                {running
                  ? isAllocation
                    ? alloc && alloc.processedGroups === 0
                      ? t('progress.feed.searchingHotels', { total: alloc.totalGroups || '…' })
                      : t('progress.feed.bookingHotels')
                    : t('progress.feed.processingPnrs')
                  : isAllocation
                    ? done
                      ? neededUnreserved > 0
                        ? t('progress.feed.allocGaps', { count: neededUnreserved })
                        : t('progress.feed.allocDoneNone')
                      : t('progress.feed.allocNoneYet')
                    : t('progress.feed.noPnrsYet')}
              </p>
            )
          ) : (
            <ul className="divide-y divide-border text-sm">
              {/* Newest-settled first — the operator watches the latest results land at
                  the top as the run progresses (rebooking only; allocation keeps order). */}
              {(isAllocation ? feed : [...feed].reverse())
                .slice(0, 100)
                .map((g) =>
                isAllocation ? (
                  <li
                    key={g.subCaseUrn}
                    className="flex items-center justify-between gap-2 px-3 py-1.5"
                  >
                    <span className="font-medium">{g.pnrLabel}</span>
                    <span className="flex items-center gap-1 text-xs font-medium text-emerald-600 dark:text-emerald-400">
                      <span aria-hidden>→</span> {t('progress.feed.hotelBooked')}
                    </span>
                  </li>
                ) : (
                  <RebookingFeedRow
                    key={g.subCaseUrn}
                    group={g}
                    outcome={outcomeByGroupId.get(g.subCaseUrn)}
                    expanded={expandedPnr === g.subCaseUrn}
                    onToggle={() =>
                      setExpandedPnr((cur) => (cur === g.subCaseUrn ? null : g.subCaseUrn))
                    }
                  />
                ),
              )}
              {feed.length > 100 ? (
                <li className="px-3 py-1.5 text-center text-xs text-muted-foreground">
                  {t('progress.feed.more', { count: feed.length - 100 })}
                </li>
              ) : null}
            </ul>
          )}
        </div>
        {/* RIGHT-SIDE DRAWER — the selected PNR's engine detail, full height, pushing the
            list left (the selected row is highlighted). Re-click the row or ✕ to close. */}
        {drawerOpen ? (
          <aside
            className="flex min-h-0 w-[46%] shrink-0 flex-col overflow-hidden rounded-md border border-border bg-muted/10"
            data-testid="pnr-detail-drawer"
          >
            <div className="flex shrink-0 items-center justify-between border-b border-border px-3 py-2">
              <span className="text-xs font-semibold text-foreground/70">
                {t('progress.drawer.title', {
                  pnr: selectedOutcome!.pnrMasked || selectedGroup!.pnrLabel,
                })}
              </span>
              <button
                type="button"
                onClick={() => setExpandedPnr(null)}
                className="rounded p-0.5 text-muted-foreground hover:bg-muted"
                aria-label={t('progress.drawer.close')}
              >
                <XCircle className="h-4 w-4" aria-hidden />
              </button>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto p-4">
              <PnrEngineDetail group={selectedGroup!} outcome={selectedOutcome!} />
            </div>
          </aside>
        ) : null}
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          {(failed || partial) && onRerun ? (
            <Button
              variant="default"
              onClick={() => {
                onRerun();
                handleOpenChange(false);
              }}
              data-testid="rebooking-progress-rerun"
            >
              <RotateCw className="mr-1.5 h-4 w-4" aria-hidden />
              {t('progress.footer.rerun')}
            </Button>
          ) : null}
          <Button
            variant={done ? 'default' : 'outline'}
            onClick={() => handleOpenChange(false)}
            data-testid="rebooking-progress-close"
          >
            {running ? t('progress.footer.closeRunning') : t('progress.footer.close')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** No-options feed line: "⊘ {reason}" + a policy link when it's policy-disabled.
 *  `reason` overrides the roster row's code (e.g. from the live settle outcome). */
function NoOptionsFeedLine({
  group,
  reason: reasonProp,
}: {
  group: DisruptionPnrGroupRow;
  reason?: string | undefined;
}) {
  const { t } = useTranslation('rebooking');
  const reason = reasonProp ?? group.noOptionsReason;
  const copy = noOptionsReasonLabel(reason, t) || t('progress.noOptions.fallback');
  return (
    <span className="flex items-center gap-1.5 text-xs text-muted-foreground" title={copy}>
      <Ban className="h-3 w-3 shrink-0 text-amber-600" aria-hidden />
      <span className="max-w-[20rem] truncate">{copy}</span>
      {reason === 'POLICY_DISABLED' && group.governingPolicyUrn ? (
        <Link
          to={`/policies/${encodeURIComponent(group.governingPolicyUrn)}`}
          className="text-primary hover:underline"
          data-testid="progress-no-options-policy-link"
        >
          {t('progress.noOptions.viewPolicy')}
        </Link>
      ) : null}
    </span>
  );
}

/** The "No options" stat tile — a total plus a per-reason breakdown. */
function NoOptionsStat({
  total,
  byReason,
}: {
  total: number;
  byReason: Array<[string, number]>;
}) {
  const { t } = useTranslation('rebooking');
  return (
    <div className="rounded-md border border-border p-3" data-testid="stat-no-options">
      <p className="text-xs uppercase text-muted-foreground">{t('progress.stat.noOptions')}</p>
      <p className={`mt-1 text-2xl font-semibold tabular-nums ${total > 0 ? 'text-amber-600' : ''}`}>
        {total}
      </p>
      {byReason.length > 0 ? (
        <ul className="mt-1 space-y-0.5 text-[11px] text-muted-foreground">
          {byReason.map(([reason, count]) => (
            <li key={reason}>
              {count} {noOptionsReasonShort(reason, t) ?? reason.toLowerCase()}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

/** Hotel-allocation progress as 0..100, from booking's run marker (groups settled / planned). */
function allocationPct(a: DisruptionProgress['allocationRun'] | null | undefined): number {
  if (!a) return 0;
  if (a.status === 'COMPLETED') return 100;
  if (a.totalGroups > 0) return clampPct(a.processedGroups / a.totalGroups);
  return 0;
}

/** Run progress as 0..100, from the durable run marker. */
function runPct(d: DisruptionProgress): number {
  // While the run is in flight, prefer the LIVE per-group counter
  // (processedGroups/plannedGroups) — it climbs group-by-group so the bar moves
  // continuously even on a single-case disruption (the frozen-bar fix). Falls back
  // to the per-case fraction when the marker predates per-group counters.
  if (d.runStatus === 'RUNNING' || d.runStatus === 'QUEUED') {
    const planned = d.plannedGroups ?? 0;
    if (planned > 0) return clampPct((d.processedGroups ?? 0) / planned);
    if (d.totalCases > 0) return clampPct(d.processedCases / d.totalCases);
    return 0;
  }
  // Terminal failure/partial: show the fraction of groups actually RESOLVED
  // (assigned + with-options) so a run that produced little doesn't read a
  // misleading 100% just because every case was "processed" (the FAILED-looks-
  // successful bug). A single-case run that exhausted retries had
  // processedCases===totalCases → would otherwise show 100%.
  if (d.runStatus === 'FAILED' || d.runStatus === 'PARTIAL') {
    const total = d.groupsTotal || 0;
    const resolved = (d.assignedGroups ?? 0) + (d.groupsWithOptions ?? 0);
    if (total > 0) return clampPct(resolved / total);
  }
  if (d.totalCases > 0) return clampPct(d.processedCases / d.totalCases);
  if (d.groupsTotal > 0) return clampPct(d.resolved / d.groupsTotal);
  return 0;
}

function clampPct(n: number): number {
  return Math.round(Math.max(0, Math.min(1, n)) * 100);
}

/** Elapsed whole seconds since a run start instant; 0 when unparseable/in the future. */
function elapsedSeconds(startedAt: string | undefined, now: number): number {
  if (!startedAt) return 0;
  const t = new Date(startedAt).getTime();
  if (Number.isNaN(t)) return 0;
  return Math.max(0, Math.floor((now - t) / 1000));
}

/** "MM:SS" count-up from a run start instant (the live elapsed clock). */
function clockText(secs: number): string {
  const mm = Math.floor(secs / 60);
  const ss = secs % 60;
  return `${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
}

/** "MM:SS" since a run start instant; '' when unparseable/in the future. The
 *  "… ago" wording lives in the bundle (`progress.activity.ago`). */
function elapsedClock(startedAt: string | undefined, now: number): string {
  if (!startedAt) return '';
  const t = new Date(startedAt).getTime();
  if (Number.isNaN(t)) return '';
  const secs = Math.floor((now - t) / 1000);
  if (secs < 0) return '';
  const mm = Math.floor(secs / 60);
  const ss = secs % 60;
  return `${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
}

/** A 1s ticking clock while `active` — drives the live elapsed counter. */
function useTickWhile(active: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [active]);
  return now;
}

/** "LA456 GRU→SCL 10:40" for the feed line; degrades gracefully on missing parts. */
function assignedFlightText(af: DisruptionPnrGroupRow['assignedFlight']): string {
  if (!af) return '—';
  const route =
    af.origin && af.destination ? `${af.origin}→${af.destination}` : (af.destination ?? '');
  const time = af.departureAt ? formatFlightTime(af.departureAt, af.origin) : '';
  return [af.number, route, time].filter(Boolean).join(' ') || '—';
}

function Stat({ label, value, tone }: { label: string; value: number | string; tone?: string }) {
  return (
    <div className="rounded-md border border-border p-3">
      <p className="text-xs uppercase text-muted-foreground">{label}</p>
      <p className={`mt-1 text-2xl font-semibold tabular-nums ${tone ?? ''}`}>{value}</p>
    </div>
  );
}

/* ── rebooking-progress-transparency (NEX-364 / U1) — the rich per-PNR MVP view ──
 * A live per-PNR feed row (fills in as groups settle over the WS) that expands into
 * the "engine reasoning" detail modelled on the frozen MVP EngineReasoningDialog /
 * PassengerEngineDetail: Original itinerary + client-synthesized narration steps +
 * the strategies list (rank-1 = RECOMMENDED, others ELIGIBLE; BLOCKED verdicts are
 * an M2 trace — an explicit placeholder slot below) + the score formula. */

function fmtScore(n: number): string {
  return (Number.isFinite(n) ? n : 0).toFixed(2);
}

/** The scoring formula itself — maths, identical in every language. */
const SCORE_FORMULA = 'finalScore = 0.4·U − 0.3·S − 0.3·C';

/** Three-line skeleton while the initial roster/detail loads (uxChecklist: loading). */
function FeedSkeleton() {
  return (
    <ul className="divide-y divide-border" data-testid="rebooking-detail-skeleton">
      {[0, 1, 2].map((i) => (
        <li key={i} className="flex items-center justify-between gap-2 px-3 py-3">
          <div className="h-3 w-24 animate-pulse rounded bg-muted" />
          <div className="h-3 w-16 animate-pulse rounded bg-muted" />
        </li>
      ))}
    </ul>
  );
}

/** The rank-1 score components, compact for the row (or full in the detail). */
function RebookingScoreLine({
  scored,
  testid,
}: {
  scored: RebookingScored;
  testid?: string;
}) {
  const { t } = useTranslation('rebooking');
  return (
    <div className="shrink-0 text-right text-[11px]" data-testid={testid}>
      <div className="font-mono font-semibold tabular-nums">
        {t('progress.score.label')} {fmtScore(scored.finalScore)}
      </div>
      <div className="font-mono tabular-nums text-muted-foreground">
        U{fmtScore(scored.urgency)} · S{fmtScore(scored.satisfaction)}
      </div>
    </div>
  );
}

/** One-line winning-strategy summary under a SELECTED/OVERRIDDEN row. Falls back to
 *  the roster's assigned flight when the settle outcome hasn't landed yet. */
function OutcomeSummary({
  group,
  outcome,
}: {
  group: DisruptionPnrGroupRow;
  outcome?: MergedOutcome | undefined;
}) {
  const { t } = useTranslation('rebooking');
  const best = outcome?.best;
  // Resolve the governing policy's NAME (the option only carries its URN — a random id).
  // usePolicies is one cached query, deduped across every row.
  const policies = usePolicies({ active: true });
  const policyName = best?.policyUrn
    ? policies.data?.find((p) => p.policyUrn === best.policyUrn)?.name
    : undefined;
  return (
    <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs">
      <span className="flex items-center gap-1 font-medium text-emerald-600 dark:text-emerald-400">
        <span aria-hidden>→</span>
        {best
          ? `${strategyKindLabel(best.kind, t)} · #${best.rank}`
          : assignedFlightText(group.assignedFlight)}
      </span>
      {best ? (
        <>
          {/* The fare difference: the airline's own reissue cost as is, a flight Nexa buys at its
              FLIGHT price — «No disponible» without a price card, never $0 (D17, D20). */}
          <span className="text-muted-foreground" data-testid="best-adc">{formatPriceMinor(best.adcMinor, 'USD')}</span>
          <span
            className={`inline-flex items-center gap-0.5 ${
              best.onwardFeasible ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-600'
            }`}
          >
            <Link2 className="h-3 w-3" aria-hidden />
            {best.onwardFeasible
              ? t('progress.outcome.connectionOk')
              : t('progress.outcome.connectionAtRisk')}
          </span>
          {best.overnight ? (
            <span className="inline-flex items-center gap-0.5 text-indigo-600 dark:text-indigo-400">
              <Moon className="h-3 w-3" aria-hidden /> {t('progress.outcome.overnight')}
            </span>
          ) : null}
          {best.policyUrn ? (
            <Link
              to={`/policies/${encodeURIComponent(best.policyUrn)}`}
              className="text-primary hover:underline"
              title={t('progress.outcome.policyTitle')}
            >
              {policyName ?? t('progress.outcome.policyFallback')}
            </Link>
          ) : null}
        </>
      ) : null}
    </div>
  );
}

/** A live per-PNR feed row: PNR + pax + winning strategy / no-options reason + the
 *  score line, expandable into the full engine-reasoning detail. */
function RebookingFeedRow({
  group,
  outcome,
  expanded,
  onToggle,
}: {
  group: DisruptionPnrGroupRow;
  outcome: MergedOutcome | undefined;
  expanded: boolean;
  onToggle: () => void;
}) {
  const { t } = useTranslation('rebooking');
  // Prefer the LIVE settle outcome status (arrives over WS as the run settles) over the
  // roster's status (only refreshed on terminal run) — so a row shows its real result
  // (SELECTED / NO_OPTIONS / OPTIONS_READY) the moment the engine settles it.
  const status = outcome?.status ?? group.rebookingStatus;
  const selected = status === 'SELECTED' || status === 'OVERRIDDEN';
  const noOptions = status === 'NO_OPTIONS' || status === 'NOT_APPLICABLE';
  const hasDetail =
    !!outcome &&
    (outcome.options.length > 0 ||
      !!outcome.original ||
      !!outcome.scored ||
      !!outcome.noOptionsReason);
  const pax = outcome?.paxCount ?? group.paxCount;
  // A group with a winning option (OPTIONS_READY *or* SELECTED) shows its RESULT — the
  // strategy + score card. Only a group with truly no result yet reads "Processing…".
  const best = outcome?.best;
  const resolved = !!best || selected || noOptions;

  return (
    <li
      data-testid={`pnr-row-${group.subCaseUrn}`}
      className={expanded ? 'bg-primary/5 ring-1 ring-inset ring-primary/40' : ''}
    >
      <div className="flex items-start justify-between gap-3 px-3 py-2">
        {/* LEFT — PNR + pax + expand toggle (+ the no-options reason when there is one). */}
        <button
          type="button"
          onClick={hasDetail ? onToggle : undefined}
          disabled={!hasDetail}
          aria-expanded={hasDetail ? expanded : undefined}
          data-testid={hasDetail ? `pnr-expand-${group.subCaseUrn}` : undefined}
          className={`flex min-w-0 flex-1 items-start gap-2 text-left ${
            hasDetail ? 'cursor-pointer' : 'cursor-default'
          }`}
        >
          {hasDetail ? (
            expanded ? (
              <ChevronDown className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
            ) : (
              <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
            )
          ) : (
            <span className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          )}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="font-medium">{group.pnrLabel}</span>
              {pax ? (
                <span className="text-xs text-muted-foreground">
                  {t('progress.pax', { count: pax })}
                </span>
              ) : null}
              {selected ? (
                <span className="rounded-full bg-emerald-500/15 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700 dark:text-emerald-400">
                  {t('progress.row.confirmed')}
                </span>
              ) : best ? (
                <span className="rounded-full bg-sky-500/15 px-1.5 py-0.5 text-[10px] font-semibold text-sky-700 dark:text-sky-400">
                  {t('progress.row.recommendedTop')}
                </span>
              ) : null}
            </div>
            {noOptions ? (
              <div className="mt-0.5">
                <NoOptionsFeedLine group={group} reason={outcome?.noOptionsReason} />
              </div>
            ) : best || selected ? (
              <OutcomeSummary group={group} outcome={outcome} />
            ) : null}
          </div>
        </button>
        {/* RIGHT — just the compact score. The winning-strategy line sits inline on the
            left; the full engine detail lives in the drawer, so the row stays narrow. */}
        {outcome?.scored ? (
          <RebookingScoreLine
            scored={outcome.scored}
            testid={`pnr-score-${group.subCaseUrn}`}
          />
        ) : !resolved ? (
          <span className="shrink-0 self-center text-xs text-muted-foreground">
            {t('progress.row.processing')}
          </span>
        ) : null}
      </div>
    </li>
  );
}

type NarrationKind =
  | 'analyzing'
  | 'vulnerable'
  | 'onward'
  | 'manifest'
  | 'filtering'
  | 'shopping'
  | 'scoring'
  | 'done';
interface NarrationStep {
  kind: NarrationKind;
  text: string;
  detail?: string;
}

/** Re-derive the engine's "innards" CLIENT-SIDE from the settle outcome (like the
 *  MVP useEngineNarration) — there is no backend step-stream in M1. */
function synthesizeNarration(
  group: DisruptionPnrGroupRow,
  outcome: MergedOutcome,
  t: TFunction<'rebooking'>,
): NarrationStep[] {
  const steps: NarrationStep[] = [];
  const cabin = outcome.original?.cabin ?? group.assignedFlight?.cabin ?? 'ECONOMY';
  const flightLabel = outcome.original?.flightLabel ?? group.flightLabel ?? group.flightNumber ?? '—';
  const pnr = outcome.pnrMasked || group.pnrLabel;

  steps.push({
    kind: 'analyzing',
    text: t('engine.narration.analyzing', { pnr }),
    detail: `${flightLabel} · ${cabin}`,
  });

  const priority = (group.priorityTags ?? [])[0] ?? (group.needs ?? [])[0];
  if (priority) {
    steps.push({
      kind: 'vulnerable',
      text: t('engine.narration.priority', { tag: priority }),
      detail: t('engine.narration.priorityDetail'),
    });
  }

  if (outcome.best || group.connection) {
    const onwardOk = outcome.best?.onwardFeasible ?? true;
    steps.push({
      kind: 'onward',
      text: onwardOk
        ? t('engine.narration.connectionOk')
        : t('engine.narration.connectionAtRisk'),
      detail: t('engine.narration.connectionDetail'),
    });
  }

  steps.push({
    kind: 'manifest',
    text: t('engine.narration.manifest', { cabin }),
    detail: t('engine.narration.manifestDetail'),
  });
  steps.push({
    kind: 'filtering',
    text: t('engine.narration.filters'),
    detail: t('engine.narration.filtersDetail'),
  });

  const n = outcome.optionCount || outcome.options.length;
  steps.push({
    kind: 'shopping',
    text: t('engine.narration.evaluating', { count: n }),
    detail: t('engine.narration.evaluatingDetail'),
  });

  if (outcome.scored) {
    steps.push({
      kind: 'scoring',
      text: t('engine.narration.score', {
        urgency: fmtScore(outcome.scored.urgency),
        satisfaction: fmtScore(outcome.scored.satisfaction),
      }),
      detail: SCORE_FORMULA,
    });
  }

  if (outcome.options.length === 0 && outcome.noOptionsReason) {
    steps.push({
      kind: 'done',
      text: t('engine.narration.noOptions'),
      detail: noOptionsReasonShort(outcome.noOptionsReason, t) ?? outcome.noOptionsReason,
    });
  } else {
    steps.push({
      kind: 'done',
      text: t('engine.narration.optionsGenerated', { count: outcome.options.length }),
      ...(outcome.best
        ? {
            detail: t('engine.narration.recommendedDetail', {
              kind: strategyKindLabel(outcome.best.kind, t),
            }),
          }
        : {}),
    });
  }
  return steps;
}

function StepIcon({ kind }: { kind: NarrationKind }) {
  const cls = 'mt-0.5 h-3.5 w-3.5 shrink-0';
  if (kind === 'vulnerable') return <ShieldAlert className={`${cls} text-rose-500`} aria-hidden />;
  if (kind === 'onward') return <Link2 className={`${cls} text-indigo-500`} aria-hidden />;
  if (kind === 'scoring' || kind === 'done')
    return <Check className={`${cls} text-emerald-500`} aria-hidden />;
  return <Check className={`${cls} text-muted-foreground`} aria-hidden />;
}

/** One strategy (option) card — MVP style. M1: every generated option is ELIGIBLE;
 *  rank-1 is the RECOMMENDED / final decision. BLOCKED verdicts + reasons are an M2
 *  trace (placeholder slot below the grid) — we do NOT fabricate them here. */
function StrategyCard({ option, recommended }: { option: RebookingOptionRow; recommended: boolean }) {
  const { t } = useTranslation('rebooking');
  return (
    <div
      className={`rounded-lg border p-2.5 text-sm ${
        recommended
          ? 'border-emerald-500 bg-emerald-500/5 shadow-[0_0_0_2px_rgba(16,185,129,0.15)]'
          : 'border-border bg-card'
      }`}
      data-testid={`strategy-card-${option.optionUrn}`}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          <span
            className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-bold ${
              recommended
                ? 'bg-emerald-500/20 text-emerald-700 dark:text-emerald-400'
                : 'bg-primary/15 text-primary'
            }`}
          >
            {rankLetter(option.rank)}
          </span>
          <span className="truncate font-semibold">{strategyKindLabel(option.kind, t)}</span>
        </div>
        {recommended ? (
          <span className="inline-flex shrink-0 items-center gap-0.5 rounded bg-emerald-500/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-emerald-700 dark:text-emerald-400">
            <Award className="h-3 w-3" aria-hidden /> {t('engine.card.recommended')}
          </span>
        ) : (
          <span className="inline-flex shrink-0 items-center gap-0.5 text-[10px] font-semibold uppercase text-muted-foreground">
            <Check className="h-3 w-3" aria-hidden /> {t('engine.card.eligible')}
          </span>
        )}
      </div>
      <div className="mt-1.5 truncate font-mono text-xs text-muted-foreground">{option.flightLabel}</div>
      <div className="mt-1 flex items-center justify-between font-mono text-xs tabular-nums">
        <span>
          {t('progress.score.label')} {fmtScore(option.finalScore)}
        </span>
        <span className="text-muted-foreground" data-testid="option-adc">{formatPriceMinor(option.adcMinor, 'USD')}</span>
      </div>
    </div>
  );
}

/** The full per-PNR "engine reasoning" detail — the MVP PassengerEngineDetail layout
 *  rendered from the REAL settle outcome. */
function PnrEngineDetail({ group, outcome }: { group: DisruptionPnrGroupRow; outcome: MergedOutcome }) {
  const { t } = useTranslation('rebooking');
  const steps = synthesizeNarration(group, outcome, t);
  const options = [...outcome.options].sort((a, b) => a.rank - b.rank);
  const recommended = options.find((o) => o.rank === 1) ?? options[0];
  const noOptions = options.length === 0;
  const pax = outcome.paxCount ?? group.paxCount;

  return (
    <div className="space-y-4" data-testid={`pnr-engine-detail-${group.subCaseUrn}`}>
      {/* Identity */}
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <Bot className="h-4 w-4 text-primary" aria-hidden />
        <span className="font-semibold">{t('engine.title')}</span>
        <span className="text-muted-foreground">· PNR {outcome.pnrMasked || group.pnrLabel}</span>
        {pax ? (
          <span className="text-xs text-muted-foreground">{t('progress.pax', { count: pax })}</span>
        ) : null}
      </div>

      {/* Final decision — placed at the TOP so it's the first thing visible when the
          drawer opens (the operator sees the outcome before scrolling into the trace). */}
      {recommended && !noOptions ? (
        <section
          data-testid="engine-final-decision"
          className="rounded-lg border-2 border-emerald-500/40 bg-emerald-500/5 p-3"
        >
          <div className="flex items-center gap-2 text-sm font-medium">
            <Award className="h-4 w-4 text-emerald-600" aria-hidden />
            {t('engine.finalDecision', {
              letter: rankLetter(recommended.rank),
              kind: strategyKindLabel(recommended.kind, t),
            })}
          </div>
          <div className="mt-1 font-mono text-sm">{recommended.flightLabel}</div>
          <div className="mt-1 flex flex-wrap gap-x-4 text-xs text-muted-foreground">
            <span className="font-mono">
              {t('progress.score.label')} {fmtScore(recommended.finalScore)}
            </span>
            <span className="font-mono" data-testid="recommended-adc">{formatPriceMinor(recommended.adcMinor, 'USD')}</span>
            {recommended.policyUrn ? (
              <Link
                to={`/policies/${encodeURIComponent(recommended.policyUrn)}`}
                className="text-primary hover:underline"
              >
                {t('progress.outcome.policyFallback')}
              </Link>
            ) : null}
          </div>
        </section>
      ) : null}

      {/* Original itinerary */}
      <section data-testid="engine-original">
        <h4 className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          {t('engine.originalItinerary')}
        </h4>
        <div className="rounded-lg border border-border bg-muted/30 p-3 text-sm">
          {/* The rebooking settle outcome doesn't carry the disrupted flight — fall back
              to the roster group's flight (the PNR's original itinerary) so this never
              reads "not available" once the roster row is joined. */}
          {(() => {
            const flightLabel = outcome.original?.flightLabel || group.flightLabel;
            const originIata = outcome.original?.originIata || group.origin;
            const destIata = outcome.original?.destIata || group.destination;
            const status = outcome.original?.status;
            const cabin = outcome.original?.cabin || group.assignedFlight?.cabin;
            return flightLabel || originIata || destIata ? (
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-mono font-bold">{flightLabel || '—'}</span>
                {status === 'DENIED_BOARDING' ? (
                  // overbooking U2 (`rebooking:IRebookingOriginalItinerary.status@v2`): the flight
                  // operated, the PNR was left on the ground — «Sobreventa», never a raw code.
                  <span
                    className="rounded bg-muted px-1.5 py-0.5 text-[11px] font-semibold uppercase text-foreground"
                    data-testid="engine-original-denied"
                  >
                    {t('disruptions:delay.badge.kind.DENIED_BOARDING')}
                  </span>
                ) : status ? (
                  <span className="rounded bg-rose-100 px-1.5 py-0.5 text-[11px] font-semibold uppercase text-rose-700 dark:bg-rose-900 dark:text-rose-400">
                    {status}
                  </span>
                ) : null}
                <span className="font-mono text-muted-foreground">
                  {originIata || '—'} → {destIata || '—'}
                </span>
                {cabin ? <span className="text-xs text-muted-foreground">{cabin}</span> : null}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">
                {t('engine.originalUnavailable')} <span className="font-mono">—</span>
              </p>
            );
          })()}
        </div>
      </section>

      {/* Steps / narration (client-synthesized) */}
      <section data-testid="engine-narration">
        <h4 className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          {t('engine.steps', { count: steps.length })}
        </h4>
        <ul className="space-y-1.5">
          {steps.map((s, i) => (
            <li key={`${s.kind}-${i}`} className="flex items-start gap-2 text-sm">
              <StepIcon kind={s.kind} />
              <div className="min-w-0">
                <span>{s.text}</span>
                {s.detail ? (
                  <span className="ml-1 font-mono text-[11px] text-muted-foreground">{s.detail}</span>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      </section>

      {/* Strategies */}
      <section data-testid="engine-strategies">
        <h4 className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          {t('engine.strategies', { count: options.length })}
        </h4>
        {noOptions ? (
          <p className="rounded-md border border-amber-500/40 bg-amber-500/10 p-2 text-xs text-amber-700 dark:text-amber-400">
            {t('engine.noEligible', {
              reason:
                noOptionsReasonLabel(outcome.noOptionsReason, t) ||
                t('engine.noEligibleFallback'),
            })}
          </p>
        ) : (
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {options.map((o) => (
              <StrategyCard key={o.optionUrn} option={o} recommended={o === recommended} />
            ))}
          </div>
        )}
        {/* M2 — BLOCKED strategies + their real reason (transit visa / MCT / no-
            inventory …) come from the engine's per-PNR evaluation trace. The
            timeline below renders the real verdicts (NEX-362 / M2). */}
        <p
          className="mb-1 mt-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground"
          data-testid="engine-blocked-placeholder"
        >
          {t('engine.blockedPlaceholder')}
        </p>
        <PnrEvaluationTimeline groupId={group.subCaseUrn} />
      </section>

      {/* Score formula with the real components */}
      {outcome.scored ? (
        <section
          data-testid="engine-score-formula"
          className="rounded-lg border border-border bg-card p-3"
        >
          <div className="mb-1 flex flex-wrap items-center gap-x-3 font-mono text-sm tabular-nums">
            <span>urgency={fmtScore(outcome.scored.urgency)}</span>
            <span>satisfaction={fmtScore(outcome.scored.satisfaction)}</span>
            <span className="font-semibold">finalScore={fmtScore(outcome.scored.finalScore)}</span>
          </div>
          <p className="text-[11px] text-muted-foreground">{SCORE_FORMULA}</p>
        </section>
      ) : null}

    </div>
  );
}
